import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import SortableTrendList from 'pages/MaintenanceTrends/Trends/SortableTrendRows'

import AddButton from 'components/AddButton'
import Table from 'components/Table'

import Api from 'services/api'

import { useStoreContext } from 'hooks/StoreContext'

import useTrends from 'hooks/useTrends'

import { LoadingSinglePanePage, screenSize } from 'styles/shared'
import { Trend } from 'types/DeviceMetric'

import { color } from 'styles/colors'

const Trends = () => {
  useTrends()

  const { trends, areTrendsLoaded } = useStoreContext()

  const [trendList, setTrendList] = useState<Array<Trend>>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (areTrendsLoaded) {
      setTrendList(trends)
    }
  }, [trends, areTrendsLoaded])

  if (!areTrendsLoaded) {
    return <LoadingSinglePanePage />
  }

  const onSave = () => {
    setIsSaving(true)

    const sortedTrendList = trendList.map((trend, index) => (
      {
        id: trend.id,
        sortPosition: index
      }
    ))

    Api.DeviceMetrics.sortTrend({ trends: sortedTrendList }).then((res) => {
      setTrendList(res)
      setIsSaving(false)
      setIsEditing(false)
    })
  }

  const arrayMove = (arrayList: Array<Trend>, oldIndex: number, newIndex: number) => {
    const element = arrayList[oldIndex]
    arrayList.splice(oldIndex, 1)
    arrayList.splice(newIndex, 0, element)
    return arrayList
  }

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    const copy = [...trendList]
    const sorted = arrayMove(copy, oldIndex, newIndex)
    const updatedTrends = sorted.map((trend: Trend, index: number) => (
      {
        ...trend,
        sortPosition: index
      }
    ))

    setTrendList(updatedTrends)
  }

  const onClose = () => {
    setTrendList(trends)
    setIsEditing(!isEditing)
  }

  return (
    <>
      <S.Header>
        { isEditing &&
          <>
            <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={onClose} />
            <Button decoration="positive" styles={{ button: { marginRight: 15 } }} enabled={!isSaving} text="Save" onPress={onSave} />
          </>
        }
        { !isEditing &&
        <>
          <Button decoration="positive" styles={{ button: { marginRight: 15 } }} onPress={() => setIsEditing(!isEditing)} >
            <S.ButtonWithIcon>
              <EditIcon data-testid="edit-sort" />
              <span> Edit Sort </span>
            </S.ButtonWithIcon>
          </Button>
        </>
        }

        <AddButton uri="/admin/trends/new" testId="Add Trend">Add Trend</AddButton>
      </S.Header>
      <Table
        items={trends}
        noDataMessage={{
          title: 'No Trend Metric',
          description: 'click Add Trend to add a trend metric'
        }}
      >
        <S.TableHeader>
          <Table.HeaderCell title="Trend Name" />
          <Table.HeaderCell title="Group/Metric Name" />
          <Table.HeaderCell title="Published State" />
        </S.TableHeader>
        <SortableTrendList items={trendList} isEditing={isEditing} onSortEndCallback={onSortEnd} />
      </Table>
    </>
  )
}

export default Trends

const S = {
  TableRow: styled(Table.Row)`
    justify-content: start;
    align-items: center;
  `,
  Header: styled.div`
    display: flex;
    justify-content: end;
    padding-bottom: 5px;
    margin: 15px 20px 5px 0;
    align-items: center;

    @media ${screenSize.small} {
      justify-content: end;
    }
  `,
  TableHeader: styled(Table.Header)`
    display: grid;
    grid-template-columns: 30% 40% 30%;
    position: sticky;
    top: 15px;
    padding: 0px 5px;
  `,
  ButtonWithIcon: styled.span`
    display: flex;
    align-items: center;
    color: ${color('newLabelText')};
    gap: 5px;
    font-weight: 500;
  `
}
